import React from "react";
import { MessageSquare, MoreVertical, Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

type Props = {
  playgroundConversationId: string;
  title: string;
  subtitle: string;
  selected: boolean;
  onClick: () => void;
  onRename?: (playgroundConversationId: string) => void;
  onDelete?: (playgroundConversationId: string) => void;
  disableActions?: boolean;
};

export function ConversationListItem({
  playgroundConversationId,
  title,
  subtitle,
  selected,
  onClick,
  onRename,
  onDelete,
  disableActions = false
}: Props) {
  return (
    <div
      className={cn(
        "group flex w-full cursor-pointer items-center gap-3 rounded-md px-2 py-2 transition-colors duration-100",
        selected ? "bg-fill-2" : "hover:bg-fill-1"
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <MessageSquare
        className={cn("size-4 shrink-0", selected ? "text-fg" : "text-fg-3")}
        aria-hidden
      />
      <div className="flex min-w-0 shrink-0 grow basis-0 flex-col items-start">
        <span
          title={title}
          className={cn(
            "w-full truncate text-left text-base",
            selected ? "font-medium text-fg" : "text-fg-2"
          )}
        >
          {title}
        </span>
        <span className="w-full text-left text-sm text-fg-3">{subtitle}</span>
      </div>

      {onRename || onDelete ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              className="flex-none"
              onClick={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                e.stopPropagation();
              }}
            >
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Conversation actions"
                title="More"
                disabled={disableActions}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100"
              >
                <MoreVertical />
              </Button>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="bottom"
            align="end"
            sideOffset={6}
            className="w-40"
            onClick={(e) => e.stopPropagation()}
          >
            <DropdownMenuItem
              disabled={!onRename || disableActions}
              onSelect={() => {
                onRename?.(playgroundConversationId);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Pencil />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem
              variant="destructive"
              disabled={!onDelete || disableActions}
              onSelect={() => {
                onDelete?.(playgroundConversationId);
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
